import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  MenuItem,
  Stack,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { FollowUpRule, FollowUpFormValues, useSaveFollowUpRule } from '../queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetWorkflowSteps, useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';
import { useGetFieldBlockByWorkflowId } from '@frontend/features/custom-fields/queries';
import { useGetTemplates } from '@frontend/features/whatsapp/hooks/useGetTemplates';
import { CaseStatus } from '@bandido/shared/constants';
import { useTranslation } from 'react-i18next';
import { TemplateConfigDialog } from './TemplateConfigDialog';

interface Props {
  agencyId: number;
  rule?: FollowUpRule;
  onClose: () => void;
}

export const FollowUpForm = ({ agencyId, rule, onClose }: Props) => {
  const { addToast } = useSnackBar();
  const mutation = useSaveFollowUpRule(agencyId, rule?.id);
  const { t } = useTranslation();
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm<FollowUpFormValues>({
    defaultValues: {
      name: '',
      description: '',
      scanWorkflow: '',
      scanStep: '',
      scanStatuses: [],
      nextWorkflow: '',
      nextStep: '',
      nextStatus: '',
      fieldBlockId: '',
      fieldName: '',
      offsetDirection: 'after',
      offsetAmount: 0,
      offsetUnit: 'days',
      templateId: '',
      templateParams: '',
      templateMessageText: '',
      isActive: true,
      sendWhatsapp: true,
    },
  });

  const selectedWorkflowId = watch('scanWorkflow');
  const selectedNextWorkflowId = watch('nextWorkflow');
  const selectedFieldBlockId = watch('fieldBlockId');
  const sendWhatsapp = watch('sendWhatsapp');

  const { data: workflowsData, isLoading: workflowsLoading, isError: workflowsError } = useGetWorkflowsByAgency({
    agencyId,
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });

  const {
    data: workflowSteps,
    isLoading: stepsLoading,
    isError: stepsError,
  } = useGetWorkflowSteps(selectedWorkflowId || undefined);

  const {
    data: nextWorkflowSteps,
    isLoading: nextStepsLoading,
    isError: nextStepsError,
  } = useGetWorkflowSteps(selectedNextWorkflowId || undefined);

  const {
    data: fieldBlocks,
    isLoading: fieldBlocksLoading,
    isError: fieldBlocksError,
  } = useGetFieldBlockByWorkflowId({ id: selectedWorkflowId || undefined });

  const {
    data: templatesData,
    isLoading: templatesLoading,
    isError: templatesError,
  } = useGetTemplates(agencyId);

  const statusOptions = useMemo(
    () => [
      { id: CaseStatus.open, name: t('cases.status.open') },
      { id: CaseStatus.inProgress, name: t('cases.status.inProgress') },
      { id: CaseStatus.needsClientAction, name: t('cases.status.needsClientAction') },
      { id: CaseStatus.needsAgentAction, name: t('cases.status.needsAgentAction') },
      { id: CaseStatus.done, name: t('cases.status.done') },
      { id: CaseStatus.canceled, name: t('cases.status.canceled') },
    ],
    [t],
  );

  const selectedFieldBlock = useMemo(() => {
    if (!selectedFieldBlockId || !fieldBlocks) return undefined;
    return fieldBlocks.find((fb) => fb.id === selectedFieldBlockId);
  }, [selectedFieldBlockId, fieldBlocks]);

  const fieldOptions = useMemo(() => {
    const fields = selectedFieldBlock?.fields || [];
    return fields.filter((f) =>
      ['date', 'timestamptz', 'timestamp'].includes(f.fieldType),
    );
  }, [selectedFieldBlock]);

  useEffect(() => {
    if (rule) {
      const cfg = (rule.customField || {}) as any;
      const statusesFromRule =
        rule.scanStatuses && rule.scanStatuses.length > 0
          ? rule.scanStatuses
          : rule.scanStatus && rule.scanStatus !== ''
            ? [rule.scanStatus]
            : [];
      reset({
        name: rule.name,
        description: rule.description || '',
        scanWorkflow: rule.scanWorkflow,
        scanStep: rule.scanStep || '',
        scanStatuses: statusesFromRule,
        nextWorkflow: rule.nextWorkflow || '',
        nextStep: rule.nextStep || '',
        nextStatus: rule.nextStatus || '',
        fieldBlockId: cfg.fieldBlockId || '',
        fieldName: cfg.fieldName || '',
        offsetDirection: cfg.offsetDirection || 'after',
        offsetAmount: cfg.offsetAmount || 0,
        offsetUnit: cfg.offsetUnit || 'days',
        templateId: cfg.templateId || '',
        templateParams: (cfg.templateParams || []).join(','),
        templateMessageText: cfg.templateMessageText || '',
        isActive: rule.isActive,
        sendWhatsapp: rule.sendWhatsapp ?? true,
      });
    }
  }, [reset, rule]);

  const prevWorkflowRef = useRef<string | undefined>(undefined);
  const prevNextWorkflowRef = useRef<string | undefined>(undefined);
  const prevFieldBlockRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // reset dependent selects only when workflow changes after initial load
    const prev = prevWorkflowRef.current;
    if (prev && selectedWorkflowId && prev !== selectedWorkflowId) {
      setValue('fieldBlockId', '');
      setValue('fieldName', '');
      setValue('scanStep', '');
    }
    prevWorkflowRef.current = selectedWorkflowId;
  }, [selectedWorkflowId, setValue]);

  useEffect(() => {
    const prev = prevNextWorkflowRef.current;
    if (prev !== undefined && prev !== selectedNextWorkflowId) {
      setValue('nextStep', '');
    }
    prevNextWorkflowRef.current = selectedNextWorkflowId;
  }, [selectedNextWorkflowId, setValue]);

  useEffect(() => {
    // reset field when block changes after initial selection
    const prev = prevFieldBlockRef.current;
    if (prev && selectedFieldBlockId && prev !== selectedFieldBlockId) {
      setValue('fieldName', '');
    }
    prevFieldBlockRef.current = selectedFieldBlockId;
  }, [selectedFieldBlockId, setValue]);

  const onSubmit = async (values: FollowUpFormValues) => {
    try {
      await mutation.mutateAsync(values);
      addToast({ message: 'Follow-up saved', color: 'success' });
      onClose();
    } catch (e) {
      addToast({ message: 'Error saving follow-up', color: 'error' });
    }
  };

  const offsetDirection = watch('offsetDirection');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={2} marginTop={1}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => <TextField {...field} label="Name" fullWidth />}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => <TextField {...field} label="Description" fullWidth multiline rows={2} />}
        />
        <Box display="flex" flexDirection="column" gap={1}>
          <Controller
            name="scanWorkflow"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Workflow"
                SelectProps={{ native: true }}
                disabled={workflowsLoading || workflowsError}
              >
                <option value="">Select workflow</option>
                {workflowsData?.items.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                  </option>
                ))}
              </TextField>
            )}
          />
          {workflowsLoading && <Typography variant="caption">Loading workflows...</Typography>}
          {workflowsError && <Typography variant="caption" color="error">Error loading workflows</Typography>}
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <Controller
            name="scanStep"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Step (optional)"
                SelectProps={{ native: true }}
                disabled={!selectedWorkflowId || stepsLoading || stepsError}
              >
                <option value="">{selectedWorkflowId ? 'No step selected' : 'Select a workflow first'}</option>
                {workflowSteps?.map((step) => (
                  <option key={step.id} value={step.id}>
                    {step.name}
                  </option>
                ))}
              </TextField>
            )}
          />
          {stepsLoading && selectedWorkflowId && <Typography variant="caption">Loading steps...</Typography>}
          {stepsError && selectedWorkflowId && <Typography variant="caption" color="error">Error loading steps</Typography>}
        </Box>
        <Controller
          name="scanStatuses"
          control={control}
          render={({ field }) => {
            const selectedIds: string[] = field.value || [];
            const selectedOptions = statusOptions.filter((opt) => selectedIds.includes(opt.id));
            const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
            const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

            return (
              <Box display="flex" flexDirection="column" gap={1}>
                <Autocomplete
                  multiple
                  options={statusOptions}
                  value={selectedOptions}
                  onChange={(_, newValue) => field.onChange(newValue.map((o) => o.id))}
                  getOptionLabel={(option) => option.name}
                  disableCloseOnSelect
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox
                        icon={checkboxIcon}
                        checkedIcon={checkboxCheckedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('cases.list.filters.status')}
                      placeholder={t('caseTriggers.form.statusPlaceholderAny')}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('caseTriggers.form.statusHelperAny')}
                </Typography>
              </Box>
            );
          }}
        />
        <Box display="flex" flexDirection="column" gap={1}>
          <Controller
            name="fieldBlockId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Field block"
                SelectProps={{ native: true }}
                disabled={!selectedWorkflowId || fieldBlocksLoading || fieldBlocksError}
              >
                <option value="">{selectedWorkflowId ? 'Select field block' : 'Select a workflow first'}</option>
                {fieldBlocks?.map((fb) => (
                  <option key={fb.id} value={fb.id}>
                    {fb.name}
                  </option>
                ))}
              </TextField>
            )}
          />
          {fieldBlocksLoading && selectedWorkflowId && <Typography variant="caption">Loading field blocks...</Typography>}
          {fieldBlocksError && selectedWorkflowId && <Typography variant="caption" color="error">Error loading field blocks</Typography>}
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <Controller
            name="fieldName"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Date field"
                SelectProps={{ native: true }}
                disabled={!selectedFieldBlockId || fieldOptions.length === 0}
              >
                <option value="">
                  {selectedFieldBlockId ? 'Select date field' : 'Select a field block first'}
                </option>
                {fieldOptions.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label || f.name}
                  </option>
                ))}
              </TextField>
            )}
          />
          {selectedFieldBlockId && fieldOptions.length === 0 && (
            <Typography variant="caption" color="error">
              No date/datetime fields available in this block
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={2}>
          <Controller
            name="offsetDirection"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Offset direction" fullWidth SelectProps={{ native: true }}>
                <option value="before">Before</option>
                <option value="after">After</option>
              </TextField>
            )}
          />
          <Controller
            name="offsetAmount"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <TextField {...field} type="number" label="Offset amount" fullWidth />}
          />
          <Controller
            name="offsetUnit"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Offset unit" fullWidth SelectProps={{ native: true }}>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </TextField>
            )}
          />
        </Box>
        <Stack spacing={1}>
          <Typography variant="subtitle2">{t('caseHandlers.form.onSuccess')}</Typography>
          <Box display="flex" gap={2}>
            <Controller
              name="nextWorkflow"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label={t('caseHandlers.form.nextWorkflow')}
                  SelectProps={{ native: true }}
                  disabled={workflowsLoading || workflowsError}
                >
                  <option value="">{t('caseHandlers.form.nextWorkflowPlaceholder')}</option>
                  {workflowsData?.items.map((wf) => (
                    <option key={wf.id} value={wf.id}>
                      {wf.name}
                    </option>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="nextStep"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label={t('caseHandlers.form.nextStep')}
                  SelectProps={{ native: true }}
                  disabled={!selectedNextWorkflowId || nextStepsLoading || nextStepsError}
                >
                  <option value="">
                    {selectedNextWorkflowId
                      ? t('caseHandlers.form.nextStepPlaceholder')
                      : t('caseHandlers.form.selectWorkflowFirst')}
                  </option>
                  {nextWorkflowSteps?.map((step) => (
                    <option key={step.id} value={step.id}>
                      {step.name}
                    </option>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="nextStatus"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label={t('caseHandlers.form.nextStatus')}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <MenuItem value="">{t('caseTriggers.form.nextStatus.keepCurrent')}</MenuItem>
                  {Object.values(CaseStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`cases.status.${status}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </Stack>
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Controller
                name="sendWhatsapp"
                control={control}
                render={({ field }) => (
                  <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                )}
              />
            }
            label={t('caseTriggers.form.sendWhatsapp')}
          />
        </Stack>
        <Stack spacing={1} sx={{ opacity: sendWhatsapp ? 1 : 0.6 }}>
          <Controller
            name="templateId"
            control={control}
            rules={{ required: sendWhatsapp }}
            render={({ field }) => (
              <Stack spacing={1}>
                <TextField
                  label="WhatsApp template"
                  value={field.value || ''}
                  InputProps={{ readOnly: true }}
                  helperText={
                    field.value
                      ? `Selected template: ${
                          templatesData?.templates?.find((tpl) => tpl.id === field.value)?.elementName || field.value
                        }`
                      : 'No template selected'
                  }
                  disabled={!sendWhatsapp || templatesLoading || templatesError}
                />
                <Button variant="outlined" onClick={() => setIsTemplateDialogOpen(true)} disabled={!sendWhatsapp}>
                  {field.value ? 'Change template' : 'Select template'}
                </Button>
              </Stack>
            )}
          />
        </Stack>

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
              label="Active"
            />
          )}
        />

        <Button variant="contained" type="submit" disabled={mutation.isLoading}>
          {rule ? 'Update' : 'Create'}
        </Button>
      </Box>

      <TemplateConfigDialog
        open={isTemplateDialogOpen}
        agencyId={agencyId}
        initialTemplateId={watch('templateId')}
        initialParams={watch('templateParams') ? watch('templateParams').split(',').filter(Boolean) : []}
        onClose={() => setIsTemplateDialogOpen(false)}
        onSave={({ templateId, params, messageText }) => {
          setValue('templateId', templateId, { shouldValidate: true });
          setValue('templateParams', params.join(','), { shouldValidate: true });
          if (messageText) {
            setValue('templateMessageText', messageText);
          }
          setIsTemplateDialogOpen(false);
        }}
      />
    </form>
  );
};

// Follow-up workflow/step selectors reuse existing workflow queries:
// - Workflow select uses useGetWorkflowsByAgency (same source as agency general settings selects).
// - Step select uses useGetWorkflowSteps once a workflow is chosen.
// The selected workflow maps to scheduled_tasks.scanWorkflow and the step maps to scheduled_tasks.scanStep. For type=case_trigger the executor ignores traditional schedule fields; it triggers solely from the custom field timestamp (fieldBlockId+fieldName -> value_date), offset config, and workflow/step/status scope.
